/**
 * COMPONENT: FILES LIST
 * Liste des fichiers d'une entité avec gestion upload/suppression
 */

import { useEffect, useState } from 'react';
import { Plus, Trash2, ExternalLink } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast, ConfirmDialog } from '../ui';
import { useFiles } from '../../hooks/useFiles';
import { UploadFileModal } from '../modals/UploadFileModal';
import type { EntityFile, EntityType } from '../../types/file';
import { formatFileSize, getFileIcon } from '../../types/file';

interface FilesListProps {
  entityType: EntityType;
  entityId: string;
}

export function FilesList({ entityType, entityId }: FilesListProps) {
  const { getFilesByEntity, uploadFile, deleteFile, getFileUrl, loading } = useFiles();
  const [files, setFiles] = useState<EntityFile[]>([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<EntityFile | null>(null);

  // Charger les fichiers
  useEffect(() => {
    loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  async function loadFiles() {
    const data = await getFilesByEntity(entityType, entityId);
    setFiles(data);
  }

  // Gérer l'upload
  async function handleUpload(file: File, name: string): Promise<boolean> {
    const result = await uploadFile({
      entity_type: entityType,
      entity_id: entityId,
      file,
      name,
    });

    if (result) {
      toast.success('Fichier ajouté avec succès');
      await loadFiles();
      return true;
    } else {
      toast.error("Erreur lors de l'ajout du fichier");
      return false;
    }
  }

  // Gérer la suppression
  async function handleDelete() {
    if (!fileToDelete) return;

    const result = await deleteFile(fileToDelete);
    if (result.ok) {
      toast.success('Fichier supprimé avec succès');
    } else {
      toast.error(result.message);
    }
    // Recharger dans les deux cas : une suppression non terminée reste affichée et se rejoue.
    await loadFiles();
    setFileToDelete(null);
  }

  // Ouvrir le fichier dans un nouvel onglet. L'onglet est ouvert pendant le
  // clic : un window.open après l'attente de l'URL signée serait bloqué par
  // certains navigateurs.
  async function handleOpenFile(file: EntityFile) {
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    const url = await getFileUrl(file.file_path);
    if (!url) {
      tab?.close();
      toast.error("Impossible d'ouvrir le fichier");
      return;
    }
    if (tab) {
      tab.location.href = url;
    } else {
      window.open(url, '_blank');
    }
  }

  // Formater la date
  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  if (loading && files.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <p className="text-sm text-zinc-500">Chargement...</p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-zinc-900">Fichiers</h2>
          <Button
            onClick={() => setIsUploadModalOpen(true)}
            size="sm"
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Ajouter un fichier
          </Button>
        </div>

        {files.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-3">📁</div>
            <p className="text-sm text-zinc-500 mb-4">Aucun fichier disponible</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsUploadModalOpen(true)}
              className="flex items-center gap-2 mx-auto"
            >
              <Plus className="h-4 w-4" />
              Ajouter le premier fichier
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left py-3 px-2 text-xs font-medium text-zinc-500 uppercase">
                    Nom
                  </th>
                  <th className="text-left py-3 px-2 text-xs font-medium text-zinc-500 uppercase">
                    Taille
                  </th>
                  <th className="text-left py-3 px-2 text-xs font-medium text-zinc-500 uppercase">
                    Date
                  </th>
                  <th className="text-right py-3 px-2 text-xs font-medium text-zinc-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {files.map((file) => (
                  <tr key={file.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{getFileIcon(file.mime_type)}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-zinc-900 truncate">{file.name}</p>
                          <p className="text-xs text-zinc-500 truncate">
                            {file.file_path.split('/').pop()}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      <span className="text-sm text-zinc-600">
                        {formatFileSize(file.file_size)}
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      <span className="text-sm text-zinc-600">{formatDate(file.created_at)}</span>
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleOpenFile(file)}
                          className="p-2 hover:bg-zinc-100 rounded transition-colors"
                          title="Ouvrir le fichier"
                        >
                          <ExternalLink className="h-4 w-4 text-zinc-600" />
                        </button>
                        <button
                          onClick={() => setFileToDelete(file)}
                          className="p-2 hover:bg-red-50 rounded transition-colors"
                          title="Supprimer le fichier"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modale d'upload */}
      <UploadFileModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSubmit={handleUpload}
      />

      {/* Confirmation de suppression */}
      <ConfirmDialog
        open={!!fileToDelete}
        onOpenChange={(open) => !open && setFileToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer le fichier"
        description={`Êtes-vous sûr de vouloir supprimer le fichier "${fileToDelete?.name}" ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        variant="destructive"
      />
    </>
  );
}
